/**
 * Custom Tools API client
 */

const BASE = (companyId: string) => `/api/companies/${companyId}/custom-tools`;

export interface CustomTool {
  id: string;
  companyId: string;
  name: string;
  description?: string;
  sourceCode: string;
  schemaJson?: Record<string, unknown>;
  status: "draft" | "active" | "disabled";
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export interface TestResult {
  toolId: string;
  name: string;
  success: boolean;
  output?: string;
  error?: string;
  durationMs: number;
  nextStep?: string;
}

export const customToolsApi = {
  list: async (companyId: string): Promise<CustomTool[]> => {
    const res = await fetch(BASE(companyId));
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  getOne: async (companyId: string, id: string): Promise<CustomTool> => {
    const res = await fetch(`${BASE(companyId)}/${id}`);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  create: async (
    companyId: string,
    dto: { name: string; description?: string; sourceCode: string },
  ): Promise<CustomTool> => {
    const res = await fetch(BASE(companyId), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(dto),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  update: async (
    companyId: string,
    id: string,
    dto: { name?: string; description?: string; sourceCode?: string },
  ): Promise<CustomTool> => {
    const res = await fetch(`${BASE(companyId)}/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(dto),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  validate: async (companyId: string, sourceCode: string): Promise<ValidationResult> => {
    const res = await fetch(`${BASE(companyId)}/validate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sourceCode }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  test: async (
    companyId: string,
    id: string,
    input: Record<string, unknown>,
  ): Promise<TestResult> => {
    const res = await fetch(`${BASE(companyId)}/${id}/test`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ input }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  deploy: async (companyId: string, id: string): Promise<CustomTool> => {
    const res = await fetch(`${BASE(companyId)}/${id}/deploy`, { method: "POST" });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  disable: async (companyId: string, id: string): Promise<CustomTool> => {
    const res = await fetch(`${BASE(companyId)}/${id}/disable`, { method: "POST" });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
};
